/* Admin — Global Offer Requests inbox.
   Lists every offer application across all publishers, sortable + filterable
   so admins can triage without opening each publisher card. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "../components/admin/AdminShell";
import { authFetch } from "../utils/adminAuth";
import RejectReasonModal from "../components/admin/RejectReasonModal";
import {
  Inbox,
  Search,
  CheckCircle,
  X,
  FileText,
  Loader2,
  ExternalLink,
  Clock,
  ShieldX,
} from "lucide-react";

const authFetch_ = async (path, opts = {}) => {
  const headers = { ...(opts.headers || {}) };
  if (!headers["Content-Type"] && opts.body && typeof opts.body === "string") {
    headers["Content-Type"] = "application/json";
  }
  const res = await authFetch(path, { ...opts, headers });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res;
};

const StatusPill = ({ status }) => {
  const map = {
    pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${map[status] || "bg-slate-800 text-slate-400 border-slate-700"}`}
      data-testid={`status-pill-${status}`}
    >
      {status === "pending" && <Clock className="w-3 h-3" />}
      {status === "approved" && <CheckCircle className="w-3 h-3" />}
      {status === "rejected" && <ShieldX className="w-3 h-3" />}
      {status}
    </span>
  );
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function AdminOfferRequestsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [sourceFilter, setSourceFilter] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const path = statusFilter
        ? `/api/admin/offer-requests?status=${encodeURIComponent(statusFilter)}`
        : `/api/admin/offer-requests`;
      const data = await authFetch_(path);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const sources = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.traffic_source_label && set.add(r.traffic_source_label));
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (sourceFilter && r.traffic_source_label !== sourceFilter) return false;
      if (!term) return true;
      return (
        (r.publisher_name || "").toLowerCase().includes(term) ||
        (r.publisher_email || "").toLowerCase().includes(term) ||
        (r.offer_name || "").toLowerCase().includes(term)
      );
    });
  }, [rows, sourceFilter, q]);

  const stats = useMemo(() => {
    const s = { pending: 0, approved: 0, rejected: 0 };
    rows.forEach((r) => { if (s[r.status] !== undefined) s[r.status] += 1; });
    return s;
  }, [rows]);

  const approve = async (r) => {
    if (!window.confirm(
      `Approve "${r.offer_name}" for ${r.publisher_email}?\nThe publisher will receive a branded approval email.`
    )) return;
    setBusy(r.id);
    try {
      await authFetch_(`/api/admin/offer-requests/${r.id}/approve`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      // Re-fetch so the row moves out of the current status filter tab
      // (Pending → Approved) rather than sticking around with a stale badge.
      await load();
    } catch (e) {
      window.alert(`Approve failed: ${e.message}`);
    }
    setBusy(null);
  };

  const rejectConfirm = async (payload) => {
    if (!rejectModal) return;
    try {
      await authFetch_(
        `/api/admin/offer-requests/${rejectModal.id}/reject`,
        { method: "POST", body: JSON.stringify(payload) }
      );
      setRejectModal(null);
      // Same rationale — pull a fresh list so the row disappears from Pending
      // and shows up under Rejected when the admin switches tabs.
      await load();
    } catch (e) {
      window.alert(`Reject failed: ${e.message}`);
    }
  };

  return (
    <AdminShell
      pageTitle="Offer Requests Inbox"
      pageDescription="Every pending offer application across all publishers, ranked by newest."
    >
      {/* Stats tiles */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { key: "pending", label: "Pending", icon: Clock, color: "text-amber-400" },
          { key: "approved", label: "Approved", icon: CheckCircle, color: "text-emerald-400" },
          { key: "rejected", label: "Rejected", icon: ShieldX, color: "text-red-400" },
        ].map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setStatusFilter(s.key)}
            className={`bg-slate-900 rounded-xl border p-4 text-left transition-colors ${
              statusFilter === s.key ? "border-slate-500" : "border-slate-800 hover:border-slate-700"
            }`}
            data-testid={`stat-${s.key}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{s.label}</div>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <div className={`text-2xl font-bold tabular-nums mt-1 ${s.color}`}>{stats[s.key]}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-3 mb-4 flex flex-wrap items-center gap-2 text-sm">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search publisher, email, or offer…"
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-slate-100"
            data-testid="search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100"
          data-testid="filter-status"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100"
          data-testid="filter-source"
        >
          <option value="">All traffic sources</option>
          {sources.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm px-3 py-2 rounded-md border border-slate-700"
          data-testid="refresh"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading requests…
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-400 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            <Inbox className="w-8 h-8 text-slate-700 mx-auto mb-2" />
            No offer requests match the current filters.
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="requests-table">
            <thead className="bg-slate-800/60 text-[10px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left p-2">Received</th>
                <th className="text-left p-2">Publisher</th>
                <th className="text-left p-2">Offer</th>
                <th className="text-left p-2">Traffic Source</th>
                <th className="text-left p-2">Proofs</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filtered.map((r) => {
                const proofCount = (r.proofs && r.proofs.length) || (r.proof_screenshot_url ? 1 : 0);
                return (
                  <tr key={r.id} className="hover:bg-slate-800/40" data-testid={`req-row-${r.id}`}>
                    <td className="p-2 text-slate-400 whitespace-nowrap">
                      <div>{timeAgo(r.created_at)}</div>
                      <div className="text-[10px] text-slate-600">{new Date(r.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="p-2 min-w-[180px]">
                      <div className="text-slate-100 font-medium">{r.publisher_name || "—"}</div>
                      <div className="text-slate-500 font-mono text-[11px]">{r.publisher_email}</div>
                    </td>
                    <td className="p-2 text-slate-200 font-medium">{r.offer_name}</td>
                    <td className="p-2">
                      <span className="inline-flex bg-slate-800 text-slate-200 border border-slate-700 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
                        {r.traffic_source_label}
                      </span>
                    </td>
                    <td className="p-2">
                      {proofCount > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(r.proofs || (r.proof_screenshot_url ? [{ url: r.proof_screenshot_url }] : [])).map((p, i) => (
                            <a
                              key={i}
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              title={p.filename || `Proof ${i + 1}`}
                              className="inline-flex items-center gap-1 bg-slate-800 border border-slate-700 hover:border-blue-500 text-blue-300 rounded px-1.5 py-0.5 text-[10px] font-medium"
                            >
                              <FileText className="w-2.5 h-2.5" /> {i + 1}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-600 text-[11px]">—</span>
                      )}
                    </td>
                    <td className="p-2"><StatusPill status={r.status} /></td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Link
                        to={`/admin/publishers?highlight=${r.publisher_id}`}
                        className="mr-2 inline-flex items-center gap-1 text-slate-400 hover:text-blue-300 text-[11px]"
                        title="Open publisher"
                        data-testid={`open-publisher-${r.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Open
                      </Link>
                      {r.status === "pending" && (
                        <>
                          <button
                            type="button"
                            disabled={busy === r.id}
                            onClick={() => approve(r)}
                            className="mr-1 inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-2 py-1 rounded text-[11px]"
                            data-testid={`approve-${r.id}`}
                          >
                            <CheckCircle className="w-3 h-3" /> Approve
                          </button>
                          <button
                            type="button"
                            disabled={busy === r.id}
                            onClick={() =>
                              setRejectModal({
                                id: r.id,
                                targetName: `${r.offer_name} · ${r.publisher_email}`,
                              })
                            }
                            className="inline-flex items-center gap-1 bg-white hover:bg-red-50 border border-red-300 text-red-700 px-2 py-1 rounded text-[11px]"
                            data-testid={`reject-${r.id}`}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <RejectReasonModal
        open={!!rejectModal}
        title="Reject offer request"
        subtitle="Pick the reason this offer application is being declined. The publisher will get a branded email explaining the decision."
        targetName={rejectModal?.targetName}
        confirmLabel="Confirm & Notify Publisher"
        onCancel={() => setRejectModal(null)}
        onConfirm={rejectConfirm}
      />
    </AdminShell>
  );
}
